import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, UserCheck, UserX, Clock, Cake, FileText, Send, IdCard, Trash2, MailWarning } from 'lucide-react';
import { PageHeader, PageLoader, StatCard, StatusBadge, CategoryBadge, Empty, fmtDateTime } from '../../components/ui';
import api from '../../services/api';

function RegistrationsChart({ data }) {
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
    days.push({ d, n: data[d] || 0 });
  }
  const max = Math.max(1, ...days.map(x => x.n));
  return (
    <div>
      <div className="flex h-32 items-end gap-[3px]">
        {days.map(x => (
          <div key={x.d} className="group relative flex-1">
            <div className="rounded-t bg-navy-700 transition-colors group-hover:bg-gold-500" style={{ height: `${Math.max(x.n ? 6 : 2, (x.n / max) * 128)}px`, opacity: x.n ? 1 : 0.25 }} />
            <span className="pointer-events-none absolute -top-7 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded bg-navy-900 px-1.5 py-0.5 text-[10px] text-white group-hover:block">
              {x.d.slice(5)}: {x.n}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-gray-400"><span>30 days ago</span><span>Today</span></div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [mail, setMail] = useState(null);

  useEffect(() => {
    api.get('/admin/dashboard').then(r => setData(r.data));
    api.get('/admin/email-status').then(r => setMail(r.data)).catch(() => {});
  }, []);

  if (!data) return <PageLoader />;
  const c = data.counts;
  const verifiedPct = c.total ? Math.round((c.active / c.total) * 100) : 0;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Overview of the member register"
        actions={<Link to="/admin/notifications/new" className="btn-gold"><Send size={16} />New notification</Link>} />

      {mail && !mail.ok && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <MailWarning size={20} className="flex-shrink-0" />
          <div><strong>Email sending is not active.</strong> {mail.reason}</div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total members" value={c.total} icon={Users} hint={`${c.newLast30} joined in the last 30 days`} />
        <StatCard label="Verified (active)" value={c.active} icon={UserCheck} tone="green" hint={`${verifiedPct}% of registrations`} />
        <StatCard label="Awaiting email verification" value={c.pending} icon={Clock} tone="amber" />
        <StatCard label="Published documents" value={c.documents} icon={FileText} tone="gold" />
      </div>

      {(c.rcuUnverified > 0 || c.deletionRequests > 0 || c.disabled > 0) && (
        <div className="mt-4 flex flex-wrap gap-3">
          {c.rcuUnverified > 0 && <Link to="/admin/members?rcuVerified=false" className="badge bg-gold-100 px-3 py-1.5 text-gold-900"><IdCard size={14} /> {c.rcuUnverified} RCU card(s) to check</Link>}
          {c.deletionRequests > 0 && <Link to="/admin/members?deletionRequested=true" className="badge bg-red-100 px-3 py-1.5 text-red-800"><Trash2 size={14} /> {c.deletionRequests} deletion request(s)</Link>}
          {c.disabled > 0 && <span className="badge bg-gray-200 px-3 py-1.5 text-gray-700"><UserX size={14} /> {c.disabled} disabled</span>}
          {c.unsubscribed > 0 && <span className="badge bg-gray-200 px-3 py-1.5 text-gray-700">{c.unsubscribed} unsubscribed</span>}
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <h2 className="mb-4 font-semibold text-navy-900">Registrations — last 30 days</h2>
          <RegistrationsChart data={data.registrations} />
        </div>
        <div className="card p-5">
          <h2 className="mb-3 flex items-center gap-2 font-semibold text-navy-900"><Cake size={18} className="text-pink-500" />Birthdays this week</h2>
          {!data.birthdays.length ? <p className="text-sm text-gray-400">No birthdays in the next 7 days</p> : (
            <ul className="space-y-2">
              {data.birthdays.map(b => (
                <li key={b.id} className="flex items-center justify-between text-sm">
                  <span className="truncate">{b.fullName} <span className="text-gray-400">· {b.className}</span></span>
                  <span className={`badge ${b.offset === 0 ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-600'}`}>
                    {b.offset === 0 ? 'Today' : b.offset === 1 ? 'Tomorrow' : new Date(2000, b.dobMonth - 1, b.dobDay).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-gray-400">Birthday emails go out automatically at 7:00 AM (Sri Lanka time).</p>
        </div>
      </div>

      <div className="card mt-6">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="font-semibold text-navy-900">Recent notifications</h2>
          <Link to="/admin/notifications" className="text-sm text-navy-700 hover:underline">View all</Link>
        </div>
        {!data.recentNotifications.length ? <Empty icon={Send} title="Nothing sent yet" /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50"><tr><th className="th">Subject</th><th className="th">Type</th><th className="th">Sent</th><th className="th">Delivered</th><th className="th">Opened</th><th className="th">Status</th></tr></thead>
              <tbody className="divide-y">
                {data.recentNotifications.map(n => (
                  <tr key={n.id} className="hover:bg-gray-50">
                    <td className="td"><Link to={`/admin/notifications/${n.id}`} className="font-medium text-navy-800 hover:underline">{n.subject}</Link></td>
                    <td className="td"><CategoryBadge category={n.category} /></td>
                    <td className="td text-gray-500">{fmtDateTime(n.sentAt)}</td>
                    <td className="td">{n.sentCount}/{n.totalCount}</td>
                    <td className="td">{n.sentCount ? `${Math.round((n.openedCount / n.sentCount) * 100)}%` : '—'}</td>
                    <td className="td"><StatusBadge status={n.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
